import { formatPropertyUnitLabel } from "../../../../../../../_shared/_logic/unit-labels"
import type { Notification } from "@kontextwerk/control-web-client"
import type { InboxCaseWorkflow } from "../../../../../../../api/kehrblatt/inbox-cases"
import {
  formatPropertyCustomerRole,
  propertyCustomerRoleLabels,
} from "../../../../../_shared/_logic/customer-roles"
import type { InboxCaseDetails } from "./contract"
import { parseInboxCaseDetails } from "./details"

export type InboxCasePresentationFact = {
  label: string
  value: string
}

export type InboxCasePresentation = {
  title: string
  intro: string | null
  facts: InboxCasePresentationFact[]
  hints: string[]
}

type PropertyCustomerRole = keyof typeof propertyCustomerRoleLabels

const textValue = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : null

const withFallback = (...values: unknown[]) => {
  for (const value of values) {
    const normalized = textValue(value)

    if (normalized) return normalized
  }

  return null
}

const facts = (items: Array<[string, unknown]>): InboxCasePresentationFact[] =>
  items
    .map(([label, value]) => {
      const normalized = textValue(value)

      return normalized
        ? { label, value: label === "Einheit" ? formatPropertyUnitLabel(normalized) : normalized }
        : null
    })
    .filter(Boolean) as InboxCasePresentationFact[]

const isKnownPropertyCustomerRole = (value: string | null): value is PropertyCustomerRole => {
  if (!value) return false

  return Object.prototype.hasOwnProperty.call(
    propertyCustomerRoleLabels,
    value.trim().toUpperCase(),
  )
}

const roleOrHintFact = (value: string | null): [string, string] | null => {
  const normalized = textValue(value)

  if (!normalized) return null

  const role = normalized.toUpperCase()

  if (isKnownPropertyCustomerRole(role)) {
    return ["Rolle", formatPropertyCustomerRole(role)]
  }

  return ["Hinweis", normalized]
}

const sourceLabel = (value: string | null) => {
  const normalized = textValue(value)

  if (!normalized) return null

  switch (normalized) {
    case "bot_appointments.wrong_recipient":
      return "Terminbot"
    case "appointment_portal.wrong_recipient":
      return "Terminportal"
    default:
      break
  }

  if (normalized.startsWith("bot_appointments.")) return "Terminbot"

  if (normalized.startsWith("appointment_portal.")) return "Terminportal"

  const lower = normalized.toLowerCase()

  if (lower.includes("google")) return "Google Kalender"

  if (lower.includes("microsoft") || lower.includes("outlook")) return "Microsoft Outlook"

  if (lower.includes("calendar")) return "Kalenderanbieter"

  if (/^[a-z0-9_.-]+$/.test(lower) && /[._]/.test(lower)) return null

  return normalized
}

const contactRunStatus = (details: InboxCaseDetails) => {
  if (details.contactOrderCanceled) return "wurde gestoppt"

  if (details.contactStepResultRecorded) return "Rückmeldung dokumentiert"

  if (details.contactStepRescheduled) return "neu geplant"

  return null
}

const workflowFactValue = (
  workflow: InboxCaseWorkflow | null,
  details: InboxCaseDetails,
  key: keyof InboxCaseWorkflow["context"],
  fallback: string | null,
) => withFallback(workflow?.context[key], fallback)

const baseWorkflowFacts = (workflow: InboxCaseWorkflow | null, details: InboxCaseDetails) =>
  facts([
    ["Auftrag", workflowFactValue(workflow, details, "work_order_label", details.workOrderLabel)],
    ["Adresse", workflowFactValue(workflow, details, "address_label", details.addressLabel)],
    [
      "Einheit",
      workflowFactValue(workflow, details, "property_unit_label", details.propertyUnitLabel),
    ],
    [
      "Ansprechpartner",
      workflowFactValue(workflow, details, "customer_label", details.customerName),
    ],
    [
      "Kontaktweg",
      [workflow?.context.contact_email, workflow?.context.contact_phone]
        .filter(Boolean)
        .join(" · "),
    ],
    ["Termin", workflowFactValue(workflow, details, "schedule_label", details.slotLabel)],
    ["Quelle", withFallback(workflow?.context.source_label, sourceLabel(details.source))],
  ])

const payloadListLength = (payload: Record<string, unknown> | null | undefined, key: string) => {
  const value = payload?.[key]

  return Array.isArray(value) ? value.length : 0
}

const wrongRecipientCleanupFacts = (workflow: InboxCaseWorkflow | null) => {
  const payload = workflow?.context.payload

  if (!payload?.wrong_recipient_customer_removed) return []

  const removedLinks = payloadListLength(
    payload,
    "wrong_recipient_removed_property_customer_link_ids",
  )
  const disabledLinks = payloadListLength(
    payload,
    "wrong_recipient_outreach_disabled_property_customer_link_ids",
  )
  const cleanupResult = removedLinks
    ? "Von Einheit entfernt"
    : disabledLinks
      ? "Für Ankündigungsplanung auf Liegenschaft gesperrt"
      : "Bereits bereinigt"

  return facts([
    ["Bereinigung", cleanupResult],
    [
      "Ansprechpartner",
      payload.wrong_recipient_orphan_customer_deleted
        ? "Als verwaister Ansprechpartner gelöscht"
        : "Bleibt für weitere Zuordnungen erhalten",
    ],
  ])
}

const contactValueLine = (label: string, value: string | null | undefined): string | null => {
  const normalized = textValue(value)

  return normalized ? `${label}: ${normalized}` : null
}

const contactComparisonLine = (
  workflow: InboxCaseWorkflow | null,
  details: InboxCaseDetails,
  kind: "existing" | "submitted",
) => {
  const comparison = workflow?.context.contact_comparison ?? null
  const email =
    kind === "existing"
      ? comparison?.existing_email?.value
      : comparison?.submitted_email?.value || details.collectedContactEmail
  const phone =
    kind === "existing"
      ? comparison?.existing_phone?.value
      : comparison?.submitted_phone?.value || details.collectedContactPhone

  return [contactValueLine("E-Mail", email), contactValueLine("Telefon", phone)]
    .filter(Boolean)
    .join(" · ")
}

export const resolveInboxCaseOverlayTitle = (todo: Notification) => {
  const details = parseInboxCaseDetails(todo)
  const verifiesCustomer = details.reviewRequirements.includes("VERIFY_CUSTOMER")
  const reviewsContactPoints =
    details.reviewRequirements.includes("REVIEW_CONTACT_POINT") ||
    details.reviewRequirements.includes("UPDATE_SOURCE_CONTACT_DATA")

  if (details.flowKey === "letter_delivery_batch") return "Terminbriefe zustellen"

  if (details.wrongRecipientReview) return "Falschen Ansprechpartner korrigieren"

  if (details.contactRecipientReview) {
    if (details.flowKey === "customer_contact_verification") return "Neue Person zuordnen"

    if (details.flowKey === "customer_contact_point_review") return "Kontaktdaten prüfen"

    if (verifiesCustomer && reviewsContactPoints) return "Neue Person und Kontaktdaten zuordnen"

    if (verifiesCustomer) return "Neue Person zuordnen"

    return "Kontaktdaten prüfen"
  }

  if (details.reviewRequirements.includes("ASSIGN_OR_CREATE_WORK_ORDER")) {
    if (details.flowKey === "unclear_appointment_feedback") return "Unklaren Terminrücklauf klären"

    return "Passenden Auftrag auswählen"
  }

  if (verifiesCustomer) return "Neue Person zuordnen"

  if (details.reviewRequirements.includes("PLAN_APPOINTMENT")) return "Termin planen"

  if (details.reviewRequirements.includes("INFORM_CUSTOMER")) return "Kunden informieren"

  return "Prüffall bearbeiten"
}

export const buildInboxCasePresentation = ({
  workflow,
  details,
  activeStepId,
}: {
  workflow: InboxCaseWorkflow | null
  details: InboxCaseDetails
  activeStepId: string | null
}): InboxCasePresentation => {
  if (activeStepId === "wrong_recipient_source_cleanup") {
    return {
      title: "Im Kehrbezirksprogramm nachtragen",
      intro:
        "Die erreichte Person sagt, dass sie nicht zuständig ist. Tragen Sie die korrigierte Ansprechpartner-Zuordnung im Kehrbezirksprogramm nach, damit der nächste Import KehrBlatt nicht wieder überschreibt.",
      facts: facts([
        ["Bisheriger Ansprechpartner", details.customerName],
        ["Aussage im Gespräch", details.userStatement],
        ["Adresse", workflowFactValue(workflow, details, "address_label", details.addressLabel)],
        [
          "Einheit",
          workflowFactValue(workflow, details, "property_unit_label", details.propertyUnitLabel),
        ],
        ["Kontaktschritte", details.contactOrderCanceled ? "wurden gestoppt" : "bitte prüfen"],
      ]),
      hints: [],
    }
  }

  if (activeStepId === "wrong_recipient_kehrblatt_cleanup") {
    return {
      title: "KehrBlatt bereinigen",
      intro:
        "Wenn das Kehrbezirksprogramm bereinigt und synchronisiert ist, entfernen Sie den falsch kontaktierten Ansprechpartner aus KehrBlatt.",
      facts: facts([
        ["Falscher Ansprechpartner", details.customerName],
        ["Aussage im Gespräch", details.userStatement],
        ["Adresse", workflowFactValue(workflow, details, "address_label", details.addressLabel)],
        [
          "Einheit",
          workflowFactValue(workflow, details, "property_unit_label", details.propertyUnitLabel),
        ],
      ]).concat(wrongRecipientCleanupFacts(workflow)),
      hints: [],
    }
  }

  if (activeStepId === "contact_point_review") {
    return {
      title: "Neue E-Mail/Telefonnummer einordnen",
      intro:
        "Ein bekannter Ansprechpartner hat eine neue oder andere E-Mail-Adresse oder Telefonnummer gemeldet. Wählen Sie je Wert, ob KehrBlatt ihn ergänzen, einen alten Wert ersetzen oder ignorieren soll.",
      facts: facts(
        [
          ["Bisheriger Ansprechpartner", details.customerName],
          ["Gesammelter Ansprechpartner", details.collectedContactName],
          ["Bisherige Kontaktdaten", contactComparisonLine(workflow, details, "existing")],
          ["Gesammelte Kontaktdaten", contactComparisonLine(workflow, details, "submitted")],
          roleOrHintFact(details.collectedContactRole),
          ["Aussage im Gespräch", details.userStatement],
          ["Kontaktlauf", contactRunStatus(details)],
        ].filter(Boolean) as Array<[string, unknown]>,
      ),
      hints: [],
    }
  }

  if (activeStepId === "contact_point_source_cleanup") {
    return {
      title: "Im Kehrbezirksprogramm nachtragen",
      intro:
        "KehrBlatt wurde aktualisiert. Tragen Sie dieselbe Änderung jetzt im Kehrbezirksprogramm nach, damit der nächste Import die Daten nicht wieder überschreibt.",
      facts: facts(
        [
          ["Ansprechpartner", details.customerName],
          ["Bisherige Kontaktdaten", contactComparisonLine(workflow, details, "existing")],
          ["Gesammelte Kontaktdaten", contactComparisonLine(workflow, details, "submitted")],
          roleOrHintFact(details.collectedContactRole),
          ["Quelle", withFallback(workflow?.context.source_label, sourceLabel(details.source))],
        ].filter(Boolean) as Array<[string, unknown]>,
      ),
      hints: [],
    }
  }

  if (activeStepId === "customer") {
    return {
      title: "Gemeldete Person einordnen",
      intro:
        "Ein Kunde hat im Terminportal oder Bot eine Person mit Kontaktdaten genannt. Prüfen Sie, ob diese Person schon als Ansprechpartner zur Liegenschaft oder Einheit existiert.",
      facts: facts([
        [
          "Auftrag",
          workflowFactValue(workflow, details, "work_order_label", details.workOrderLabel),
        ],
        ["Adresse", workflowFactValue(workflow, details, "address_label", details.addressLabel)],
        [
          "Einheit",
          workflowFactValue(workflow, details, "property_unit_label", details.propertyUnitLabel),
        ],
        ["Name", details.customerName],
        ["E-Mail", details.contactEmail],
        ["Telefon", details.contactPhone],
      ]),
      hints: [],
    }
  }

  if (activeStepId === "work_order") {
    return {
      title: "Passenden Auftrag auswählen",
      intro:
        "KehrBlatt konnte den Auftrag zu dieser Rückmeldung nicht sicher finden. Wählen Sie den passenden offenen Auftrag aus oder legen Sie ihn über das Plus neu an.",
      facts: baseWorkflowFacts(workflow, details),
      hints: [],
    }
  }

  if (activeStepId === "schedule_event_resolution") {
    return {
      title: details.scheduleEventId ? "Terminwunsch übernehmen" : "Termin setzen",
      intro: details.scheduleEventId
        ? "Wandeln Sie den Kalender-Blocker in einen Auftragstermin um oder geben Sie ihn frei."
        : "Prüfen Sie den Terminwunsch und speichern Sie einen passenden Termin.",
      facts: baseWorkflowFacts(workflow, details),
      hints: [],
    }
  }

  return {
    title: workflow?.context.problem || "Prüffall prüfen",
    intro: workflow?.context.recommendation || details.descriptionLines[0] || null,
    facts: baseWorkflowFacts(workflow, details),
    hints: [],
  }
}
